<template>
  <form-section
    :title="$t('Automations_Title_Preferences')"
    :description="$t('Automations_Title_Preferences_Description')"
  >
    <draggable-table>
      <el-table
        :show-header="false"
        class="table"
        :data="data"
        stripe
        v-loading="addingInProgress"
      >
        <el-table-column>
          <template slot-scope="scope">
            {{ formatSelectedPreference(scope.row) }}
          </template>
        </el-table-column>

        <el-table-column width="40" align="center">
          <template>
            <i class="el-icon-rank"></i>
          </template>
        </el-table-column>

        <el-table-column width="40" align="center">
          <template slot-scope="scope">
            <i class="el-icon-delete" @click="deletePref(scope.$index)"></i>
          </template>
        </el-table-column>
      </el-table>
    </draggable-table>

    <el-button
      class="m-top-20"
      icon="el-icon-circle-plus-outline"
      type="primary"
      plain
      @click="showSelect()"
      data-qa="add-preference-button"
    >
      {{ $t('Audiences_Segment_Button_AddPreference') }}
    </el-button>

    <el-select
      v-show="selectVisible"
      v-model="newPref"
      ref="select"
      @visible-change="selectVisibleChange"
      @change="addPref"
      :loading="selectLoading"
      :placeholder="$t('Audiences_Segment_Text_SelectPreference')"
      filterable
      remote
      :remote-method="fetchPreferencesRemote"
      data-qa="select-preference-dropdown"
    >
      <el-option
        v-if="preferencesAvailable"
        value="all"
        key="all_preferences"
        :label="$t('AttributesList_AllPreferences')"
        data-qa="all-preferences-option"
      />
      <el-option-group
        :key="'grp-' + index"
        v-if="preferencesAvailable && item.preferences.length > 0"
        v-for="(item, index) in availableFetchedPreferences"
        :label="item.label"
      >
        <el-option
          v-for="(preference, prefIndex) in item.preferences"
          :key="'pref-' + prefIndex"
          :label="preference.name"
          :value="preference.id"
          data-qa="preference-option"
        />
      </el-option-group>
    </el-select>
  </form-section>
</template>

<script>
import { cloneDeep } from 'lodash'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import DraggableTable from '@/common/components/table/DraggableTable'
import FormSection from '@/common/components/form/FormSection'

export default {
  name: 'ConsentPreferencesListDraggable',

  components: { DraggableTable, FormSection },

  props: {
    computedAttributes: {
      type: Boolean,
      required: false,
      default: false
    },
    value: {
      required: true
    }
  },

  data() {
    return {
      data: [],
      preferences: [],
      newPref: null,
      selectVisible: false,
      selectLoading: false,
      addingInProgress: false,
      search: ''
    }
  },

  created() {
    this.fetchPreferences().then(_ => {
      this.data = (this.value || []).map(id => {
        let name = ''
        let channelName = ''
        this.preferences.forEach(channel => {
          if (channel.preferences !== undefined) {
            channel.preferences.forEach(pref => {
              if (pref.id === id) {
                name = pref.name
                channelName = channel.key
              }
            })
          }
        })
        return {
          id: id,
          name: name,
          channel: channelName
        }
      })
    })
  },

  computed: {
    preferencesAvailable() {
      let available = false
      this.availableFetchedPreferences.forEach(channelData => {
        available = available || channelData.preferences.length > 0
      })
      return available
    },

    availableFetchedPreferences() {
      let preferences = cloneDeep(this.preferences)
      if (preferences.length > 0) {
        preferences.map(channelData => {
          if (channelData.preferences === undefined) {
            channelData.preferences = []
          }
          channelData.preferences = channelData.preferences.filter(pref => {
            let included = false
            this.data.forEach(item => {
              included = included || item.id === pref.id
            })
            return !included
          })
          return channelData
        })
      }
      return preferences
    },

    preferencesEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `consent-preferences`
      })
    }
  },

  methods: {
    showSelect() {
      this.search = ''
      this.fetchPreferencesRemote('')
      this.selectVisible = true
      setTimeout(() => {
        this.$refs['select'].focus()
      }, 0)
    },

    selectVisibleChange(visible) {
      if (!visible) {
        this.selectVisible = false
      }
    },

    addPref(attr) {
      this.addingInProgress = true
      if (attr === 'all') {
        this.data = []
        this.preferences.forEach(channelData => {
          if (channelData.preferences !== undefined) {
            channelData.preferences.forEach(pref => {
              this.data.push({
                name: pref.name,
                channel: channelData.key,
                id: pref.id
              })
            })
          }
        })
        this.addingInProgress = false
      } else {
        let name = ''
        let selectedChannel = ''
        this.preferences.forEach(channelData => {
          if (channelData.preferences !== undefined) {
            channelData.preferences.forEach(pref => {
              if (pref.id === attr) {
                name = pref.name
                selectedChannel = channelData.key
              }
            })
          }
        })
        this.data.push({
          name: name,
          channel: selectedChannel,
          id: attr
        })
        this.addingInProgress = false
      }

      this.newPref = null
    },

    deletePref(index) {
      this.data.splice(index, 1)
    },

    fetchPreferences() {
      return new Promise((resolve, reject) => {
        this.selectLoading = true
        let params = { limit: 500 }
        RestApi({
          ...this.preferencesEndpoint,
          params: params
        })
          .then(({ data }) => {
            this.preferences = data
            this.selectLoading = false
            this.addingInProgress = false
            resolve()
          })
          .catch(error => {
            this.selectLoading = false
            this.addingInProgress = false
            this.$message({
              message: error.message,
              type: 'error'
            })
            reject(error)
          })
      })
    },

    fetchPreferencesRemote(query) {
      this.search = query

      let params = { limit: 500 }

      if (query !== null && query !== '') {
        params.name = query
      }

      RestApi({
        ...this.preferencesEndpoint,
        params: params
      })
        .then(({ data, headers }) => {
          this.preferences = data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    formatSelectedPreference(row) {
      let name = row.name
      let channel = row.channel
      if (!channel) {
        channel = 'No channel'
      }

      return name + ' (' + channel + ')'
    }
  },

  watch: {
    data: {
      handler: function(value) {
        let ids = []
        value.forEach(item => {
          ids.push(item.id)
        })
        this.$emit('input', ids)
      },
      deep: true
    }
  }
}
</script>

<style scoped lang="scss">
/deep/ .el-table {
  border-radius: 4px;
  border: 1px solid #dbdee7;
}

/deep/ .el-select input {
  height: 36px;
}
</style>



// WEBPACK FOOTER //
// src/modules/automations/components/ConsentPreferencesListDraggable.vue